import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, GraduationCap, Search, Send } from 'lucide-react';
import { learnApi } from '@/api/endpoints';
import type { ApiCourseLevel, LearnPostSummary } from '@/api/types';
import { LearnPostCard } from '@/components/learn/LearnPostCard';
import { matchesSearch, postsOfTopic } from '@/components/learn/learnHub';
import site from '@/config/site.json';
import { TOPICS } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { rememberLearnSignupSource, trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { LEARN_INDEX_SEO } from '@/lib/seoText';
import { coursePath, LEARN_INDEX_PATH, learnIndexStructuredData } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';

const LEVELS: ApiCourseLevel[] = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED'];

/**
 * /learn — darsliklar mavzu bo'yicha guruhlangan. Qidiruv va daraja filtri faqat client'da:
 * prerender HTML da hamma postlar bor (ichki havolalar), filtr URL ni o'zgartirmaydi.
 */
export function Learn() {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const initial = useInitialData();
  const [posts, setPosts] = useState<LearnPostSummary[] | null>(
    initial?.path === LEARN_INDEX_PATH ? (initial.learnPosts ?? []) : null,
  );
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<ApiCourseLevel | null>(null);

  useEffect(() => {
    trackEvent('learn_hub_viewed');
    rememberLearnSignupSource();
    if (posts !== null) return;
    let cancelled = false;
    learnApi
      .list()
      .then((res) => {
        if (!cancelled) setPosts(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSeo({
    title: LEARN_INDEX_SEO.title,
    description: LEARN_INDEX_SEO.description,
    path: LEARN_INDEX_PATH,
    // Bo'sh ro'yxat — yupqa kontent (sitemap'da ham yo'q)
    noindex: posts !== null && posts.length === 0,
    jsonLd: learnIndexStructuredData(),
  });

  const levels = LEVELS.filter((lv) => posts?.some((p) => p.level === lv));
  const visible = posts?.filter((p) => (!level || p.level === level) && matchesSearch(p, query)) ?? [];
  // Maqolasi yo'q mavzular ko'rinmaydi
  const sections = [
    ...TOPICS.map((tp) => ({
      key: tp.slug as string,
      title: l(tp.title),
      icon: <tp.icon className="h-5 w-5 text-brand-600" aria-hidden />,
      items: postsOfTopic(visible, tp.slug),
    })),
    { key: 'other', title: t('learn.otherTopic'), icon: null, items: postsOfTopic(visible, null) },
  ].filter((section) => section.items.length > 0);
  const telegram = site.social.telegram;
  const filtered = query.trim() !== '' || level !== null;

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {t('nav.learn')}
          </li>
        </ol>
      </nav>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold sm:text-3xl">{t('learn.title')}</h1>
          <p className="mt-2 max-w-3xl text-content-secondary">{t('learn.subtitle')}</p>
        </div>
        {telegram && (
          <a
            href={telegram}
            target="_blank"
            rel="noopener noreferrer"
            className="card flex shrink-0 items-center gap-3 px-4 py-3 text-sm transition-colors hover:border-brand-500/40"
          >
            <Send className="h-5 w-5 shrink-0 text-[#229ED9]" />
            <span>
              <span className="block text-content-secondary">{t('learn.telegramCta')}</span>
              <span className="font-semibold text-brand-600">{t('learn.telegramJoin')} →</span>
            </span>
          </a>
        )}
      </div>

      <Link
        to={coursePath(site.sqlCourseSlug)}
        onClick={() => trackEvent('article_course_link_clicked', { from: 'learn_hub' })}
        className="card card-hover mb-8 flex items-center gap-4 p-5"
      >
        <GraduationCap className="h-8 w-8 shrink-0 text-brand-600" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">{t('learn.beginnerTitle')}</span>
          <span className="mt-0.5 block text-sm text-content-secondary">{t('learn.beginnerDesc')}</span>
        </span>
        <span className="hidden shrink-0 text-sm font-semibold text-brand-600 sm:block">{t('learn.beginnerCta')} →</span>
      </Link>

      <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative w-full lg:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('learn.searchPlaceholder')}
            aria-label={t('learn.searchPlaceholder')}
            className="input pl-10"
          />
        </div>
        {levels.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('learn.levelFilter')}>
            <FilterChip active={level === null} onClick={() => setLevel(null)}>
              {t('learn.all')}
            </FilterChip>
            {levels.map((lv) => (
              <FilterChip key={lv} active={level === lv} onClick={() => setLevel(lv)}>
                {t(`courses.level.${lv}`)}
              </FilterChip>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('learn.loadError')}
        </div>
      ) : posts === null ? (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="card h-44 animate-pulse" />
          ))}
        </ul>
      ) : sections.length === 0 ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          <p>{filtered ? t('learn.noResults') : t('learn.empty')}</p>
          {filtered && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setLevel(null);
              }}
              className="mt-3 font-semibold text-brand-600 hover:underline"
            >
              {t('learn.resetFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.key} aria-labelledby={`learn-topic-${section.key}`}>
              <h2 id={`learn-topic-${section.key}`} className="mb-4 flex items-center gap-2 text-xl font-semibold">
                {section.icon}
                {section.title}
              </h2>
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {section.items.map((p) => (
                  <li key={p.slug}>
                    <LearnPostCard post={p} hideTopic />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'tag transition-colors',
        active ? 'border-brand-500 bg-brand-500/10 text-brand-600' : 'hover:text-brand-600',
      )}
    >
      {children}
    </button>
  );
}
